import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Loader2, RefreshCw, Store, ExternalLink, Plug, Search, Sparkles,
  Mail, Phone, PhoneCall, MessageCircle, Zap, Workflow, Briefcase,
  MapPin, Bot, Send, CreditCard, FileText, FolderOpen, FileSpreadsheet,
  BarChart3, Mic, ArrowRight, Check, Lock, Layers,
} from "lucide-react";
import Sidebar from "../components/Sidebar";
import ErrorState from "../components/ErrorState";
import LangToggle from "../components/LangToggle";
import ThemeToggle from "../components/ThemeToggle";
import Logo from "../components/Logo";
import IntegrationConfigModal from "../components/IntegrationConfigModal";
import { useLang } from "../i18n/LangContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICON_MAP = {
  Mail, Phone, PhoneCall, MessageCircle, Zap, Workflow, Briefcase, Sparkles,
  MapPin, Bot, Send, CreditCard, FileText, FolderOpen, FileSpreadsheet, BarChart3,
  Mic, Search, Plug,
};

const STATUS_META = {
  connected:   { cls: "bg-[#DCFCE7] text-[#16A34A]" },
  mocked:      { cls: "bg-[#FEF3C7] text-[#B45309]" },
  configure:   { cls: "bg-[#FEE2E2] text-[#DC2626]" },
  coming_soon: { cls: "bg-[#F3F4F6] text-[#6B7280]" },
};

export default function AppMarketplace() {
  const { t, lang } = useLang();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [activeTier, setActiveTier] = useState("all");
  const [configModal, setConfigModal] = useState(null);

  const STATUS_LABELS = {
    connected:   t("integ.status.connected"),
    mocked:      t("integ.status.mocked"),
    configure:   t("integ.status.configure"),
    coming_soon: t("mkt.status_coming_soon"),
  };

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const { data: d } = await axios.get(`${API}/marketplace`);
      setData(d);
    } catch (e) {
      setError(e?.message || "Failed");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const visibleTiers = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    return data.tiers
      .filter((tier) => activeTier === "all" || tier.id === activeTier)
      .map((tier) => ({
        ...tier,
        apps: tier.apps.filter((a) => !q || `${a.name} ${a.category} ${a.desc}`.toLowerCase().includes(q)),
      }))
      .filter((tier) => tier.apps.length > 0);
  }, [data, search, activeTier]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--al-bg)] text-[var(--al-text)]">
        <Sidebar />
        <div className="ml-[var(--al-sidebar-w,260px)] flex items-center justify-center h-screen">
          <Loader2 className="w-8 h-8 animate-spin text-[#3B82F6]" />
        </div>
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="min-h-screen bg-[var(--al-bg)] text-[var(--al-text)]">
        <Sidebar />
        <div className="ml-[var(--al-sidebar-w,260px)] px-8 py-16">
          <ErrorState testid="marketplace-error" title={t("mkt.error_title")} message={error} onRetry={load} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--al-bg)] text-[var(--al-text)]">
      <Sidebar />
      <div className="ml-[var(--al-sidebar-w,260px)]">
        <header
          data-testid="marketplace-header"
          className="sticky top-0 z-30 bg-[var(--al-card-bg)] border-b border-[var(--al-border)] px-8 py-4 flex items-center justify-between"
        >
          <div className="flex items-center gap-4">
            <Logo className="w-9 h-9 object-contain" />
            <div>
              <h1 className="text-[16px] font-semibold text-[var(--al-text)]">{t("mkt.title")}</h1>
              <p className="text-[12.5px] text-[var(--al-text-muted)] mt-0.5">{t("mkt.subtitle")}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <LangToggle />
            <button
              type="button"
              data-testid="mkt-refresh"
              onClick={load}
              className="flex items-center gap-1.5 px-3 h-9 rounded-lg border border-[var(--al-border)] bg-[var(--al-card-bg)] text-[12.5px] hover:border-[#3B82F6] hover:text-[#3B82F6]"
            >
              <RefreshCw className="w-3.5 h-3.5" />
            </button>
          </div>
        </header>

        <main className="px-6 lg:px-10 py-6 max-w-[1400px] mx-auto space-y-6">
          {/* Hero */}
          <section data-testid="mkt-hero" className="al-card p-6 bg-gradient-to-br from-[#1A1A2E] to-[#0F172A] text-white border-[#1A1A2E]">
            <div className="flex items-start gap-4 flex-wrap">
              <div className="w-14 h-14 rounded-2xl bg-white/10 flex items-center justify-center shrink-0">
                <Store className="w-7 h-7" />
              </div>
              <div className="flex-1 min-w-[280px]">
                <div className="inline-flex items-center gap-1.5 text-[11px] font-bold bg-white/10 px-2 py-0.5 rounded-full mb-2">
                  <Layers className="w-3 h-3" /> {t("mkt.eyebrow")}
                </div>
                <h2 className="text-[22px] sm:text-[26px] font-bold leading-tight tracking-tight">
                  {t("mkt.hero_title")}
                </h2>
                <p className="text-[13px] text-white/75 mt-2 leading-relaxed max-w-[640px]">
                  {t("mkt.hero_body")}
                </p>
                {/* Connection-health progress bar — was missing previously */}
                <div className="mt-4 max-w-[520px]" data-testid="mkt-health">
                  <div className="flex items-center justify-between text-[10.5px] font-bold uppercase tracking-wider text-white/60 mb-1">
                    <span>{t("mkt.kpi_connected")} · {data.connected} / {data.total_apps}</span>
                    <span>{Math.round((data.connected / Math.max(1, data.total_apps)) * 100)}%</span>
                  </div>
                  <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-[#34D399] to-[#10B981] transition-all" style={{ width: `${(data.connected / Math.max(1, data.total_apps)) * 100}%` }} />
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3 ml-auto">
                <Counter label={t("mkt.kpi_total")}        value={data.total_apps}     color="#FFFFFF" />
                <Counter label={t("mkt.kpi_connected")}    value={data.connected}      color="#34D399" />
                <Counter label={t("mkt.kpi_to_configure")} value={data.to_configure}   color="#FBBF24" />
              </div>
            </div>
          </section>

          {/* Recommended for you — surfaces top 3 unconnected apps from the
              cheapest tier (Essentials) so the user always has a clear next
              integration step. */}
          {(() => {
            const recs = [];
            for (const tier of (data.tiers || [])) {
              for (const app of (tier.apps || [])) {
                if (app.status !== "connected" && app.status !== "coming_soon") {
                  recs.push({ tier, app });
                  if (recs.length >= 4) break;
                }
              }
              if (recs.length >= 4) break;
            }
            if (recs.length === 0) return null;
            return (
              <section data-testid="mkt-recommended" className="al-card p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles className="w-4 h-4 text-[#F59E0B]" />
                  <h3 className="text-[13.5px] font-bold tracking-tight">
                    {lang === "pt" ? "Recomendado para você" : "Recommended for you"}
                  </h3>
                  <span className="text-[10.5px] uppercase tracking-wider text-[var(--al-text-muted)] font-semibold">
                    {lang === "pt" ? "Próximo passo" : "Next step"}
                  </span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  {recs.map(({ app, tier }) => {
                    const Icon = ICON_MAP[app.icon] || Plug;
                    return (
                      <button key={app.key} type="button"
                        data-testid={`mkt-rec-${app.key}`}
                        onClick={() => setConfigModal(app)}
                        className="al-card p-3 text-left hover:border-[#3B82F6] flex items-center gap-2.5 transition-colors">
                        <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                          style={{ background: `${tier.color}1A`, color: tier.color }}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-[12.5px] font-bold truncate">{app.name}</div>
                          <div className="text-[10px] uppercase tracking-wider text-[var(--al-text-muted)] font-semibold truncate">{app.category}</div>
                        </div>
                        <ArrowRight className="w-3.5 h-3.5 text-[#3B82F6] shrink-0" />
                      </button>
                    );
                  })}
                </div>
              </section>
            );
          })()}

          {/* Filters */}
          <section data-testid="mkt-filters" className="al-card p-3 sticky top-[84px] z-20">
            <div className="flex items-center gap-2 mb-3">
              <div className="relative flex-1">
                <Search className="w-3.5 h-3.5 text-[var(--al-text-muted)] absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  data-testid="mkt-search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t("mkt.search_placeholder")}
                  className="al-input h-9 pl-8 text-[12.5px] w-full"
                />
              </div>
            </div>
            <div className="flex items-center gap-1.5 flex-wrap" data-testid="mkt-tiers">
              <TierPill id="all" active={activeTier === "all"} color="#6B7280" label={t("mkt.tier_all")} count={data.total_apps} onClick={() => setActiveTier("all")} />
              {data.tiers.map((tier) => (
                <TierPill
                  key={tier.id}
                  id={tier.id}
                  active={activeTier === tier.id}
                  color={tier.color}
                  label={`${t(`mkt.tier_${tier.id}_short`)} (${tier.level})`}
                  count={tier.count}
                  onClick={() => setActiveTier(tier.id)}
                />
              ))}
            </div>
          </section>

          {/* Tier sections */}
          {visibleTiers.length === 0 ? (
            <div className="al-card p-10 text-center text-[13px] text-[var(--al-text-muted)]">
              {t("mkt.empty_filtered")}
            </div>
          ) : visibleTiers.map((tier) => (
            <section
              key={tier.id}
              data-testid={`mkt-tier-${tier.id}`}
              className="al-card p-0 overflow-hidden"
            >
              <header
                className="px-5 py-4 flex items-center gap-3 flex-wrap border-b"
                style={{ background: `${tier.color}10`, borderColor: `${tier.color}30` }}
              >
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center font-extrabold text-white shrink-0"
                  style={{ background: tier.color }}
                >
                  {tier.level}
                </div>
                <div className="flex-1 min-w-[260px]">
                  <h3 className="text-[15px] font-bold" style={{ color: tier.color }}>
                    {lang === "pt" ? tier.title_pt : tier.title_en}
                  </h3>
                  <p className="text-[12px] text-[var(--al-text-muted)] mt-0.5">
                    {lang === "pt" ? tier.subtitle_pt : tier.subtitle_en}
                  </p>
                </div>
                <div className="flex items-center gap-2 text-[11px] font-semibold">
                  <span className="px-2 py-0.5 rounded-full bg-[#DCFCE7] text-[#16A34A]">
                    {tier.connected_count} {t("mkt.tier_connected")}
                  </span>
                  <span className="px-2 py-0.5 rounded-full bg-[var(--al-bg)] text-[var(--al-text-muted)]">
                    {tier.count - tier.connected_count} {t("mkt.tier_pending")}
                  </span>
                </div>
              </header>
              <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {tier.apps.map((app) => (
                  <MarketplaceCard
                    key={app.key}
                    app={app}
                    tier={tier}
                    statusLabel={STATUS_LABELS[app.status] || STATUS_LABELS.configure}
                    statusCls={STATUS_META[app.status]?.cls || STATUS_META.configure.cls}
                    onInstall={() => app.status !== "coming_soon" && setConfigModal(app)}
                  />
                ))}
              </div>
            </section>
          ))}
        </main>
      </div>

      <IntegrationConfigModal
        open={!!configModal}
        integration={configModal}
        onClose={() => setConfigModal(null)}
        onSaved={async () => { await load(); }}
      />
    </div>
  );
}

function Counter({ label, value, color }) {
  return (
    <div className="text-right">
      <div className="text-[10px] uppercase tracking-wider font-semibold opacity-75">{label}</div>
      <div className="text-[22px] font-extrabold tabular-nums" style={{ color }}>{value}</div>
    </div>
  );
}

function TierPill({ id, active, color, label, count, onClick }) {
  return (
    <button
      type="button"
      data-testid={`mkt-tier-pill-${id}`}
      onClick={onClick}
      className={
        "inline-flex items-center gap-1.5 px-2.5 h-7 rounded-full text-[11.5px] font-semibold border transition-colors " +
        (active
          ? "text-white border-transparent"
          : "bg-white text-[var(--al-text-muted)] border-[var(--al-border)] hover:text-[var(--al-text)]")
      }
      style={active ? { background: color } : undefined}
    >
      {label}
      <span
        className={
          "inline-flex items-center justify-center min-w-[18px] h-4 px-1 rounded-full text-[9.5px] font-bold " +
          (active ? "bg-white/30 text-white" : "bg-[var(--al-bg)] text-[var(--al-text-muted)]")
        }
      >
        {count}
      </span>
    </button>
  );
}

function MarketplaceCard({ app, tier, statusLabel, statusCls, onInstall }) {
  const { t } = useLang();
  const Icon = ICON_MAP[app.icon] || Plug;
  const isConnected = app.status === "connected";
  const isComing = app.status === "coming_soon";
  return (
    <div
      data-testid={`mkt-card-${app.key}`}
      className={`al-card p-4 flex flex-col h-full transition-colors ${isComing ? "opacity-60" : "hover:border-[#3B82F6]"}`}
    >
      <div className="flex items-start gap-3 mb-2">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
          style={{ background: `${tier.color}1A`, color: tier.color }}
        >
          <Icon className="w-5 h-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <h4 className="text-[14px] font-bold text-[var(--al-text)] truncate">{app.name}</h4>
            {!isComing && (
              <a
                href={app.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[var(--al-text-muted)] hover:text-[#3B82F6] shrink-0"
              >
                <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
          <span className="text-[10.5px] uppercase tracking-wider font-semibold text-[var(--al-text-muted)]">
            {app.category}
          </span>
        </div>
        <span className={`shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-[9.5px] font-bold uppercase tracking-wider ${statusCls}`}>
          {statusLabel}
        </span>
      </div>

      <p className="text-[12px] text-[var(--al-text)] line-clamp-3 leading-snug mb-3">
        {app.desc}
      </p>

      <button
        type="button"
        data-testid={`mkt-install-${app.key}`}
        disabled={isComing}
        onClick={onInstall}
        className={
          "mt-auto inline-flex items-center justify-center gap-1.5 h-8 px-3 rounded-lg text-[11.5px] font-semibold transition-colors disabled:cursor-not-allowed " +
          (isConnected
            ? "border border-[#16A34A] bg-[#DCFCE7] text-[#16A34A] hover:bg-[#BBF7D0]"
            : isComing
              ? "border border-[var(--al-border)] bg-[var(--al-bg)] text-[var(--al-text-muted)]"
              : "al-btn-primary")
        }
      >
        {isComing
          ? <><Lock className="w-3 h-3" /> {t("mkt.coming_soon")}</>
          : isConnected
            ? <><Check className="w-3 h-3" /> {t("mkt.installed")}</>
            : <><ArrowRight className="w-3 h-3" /> {t("mkt.install")}</>}
      </button>
    </div>
  );
}
